import axios from "axios";

import { fetchAuthSession } from "aws-amplify/auth";
import { getApiBaseRoot } from "../config/api";

const API_BASE_ROOT = getApiBaseRoot();
const API_BASE = `${API_BASE_ROOT.replace(/\/+$/, "").replace(/\/api\/v1$/, "")}/api/v1`;

const getAuthToken = async () => {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
};

const authHeaders = async (extra = {}) => ({
  Authorization: `Bearer ${await getAuthToken()}`,
  ...extra,
});

export const AdminService = {
  async getAdminGroups() {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.payload?.["cognito:groups"] || [];
  },

  // Clients
  async listClients() {
    const response = await axios.get(`${API_BASE}/admin/clients/`, {
      headers: await authHeaders(),
    });
    return response.data;
  },
  async createClient(input) {
    const response = await axios.post(`${API_BASE}/admin/clients/`, input, {
      headers: await authHeaders({ "Content-Type": "application/json" }),
    });
    return response.data;
  },
  async updateClientCredits(email, totalCredits, moduleNames) {
    const response = await axios.patch(
      `${API_BASE}/admin/clients/${encodeURIComponent(email)}/credits/`,
      { total_credits: totalCredits, module_names: moduleNames },
      { headers: await authHeaders({ "Content-Type": "application/json" }) },
    );
    return response.data;
  },
  async getClientUsage(email) {
    const response = await axios.get(
      `${API_BASE}/admin/clients/${encodeURIComponent(email)}/usage/`,
      { headers: await authHeaders() },
    );
    return response.data;
  },

  // Ingestion / data status
  async getIngestionStatus() {
    const response = await axios.get(`${API_BASE}/admin/ingestion/status/`, {
      headers: await authHeaders(),
    });
    return response.data;
  },
  async resetIngestionCollection(collectionName) {
    const response = await axios.post(
      `${API_BASE}/admin/ingestion/reset/`,
      { collection_name: collectionName },
      { headers: await authHeaders({ "Content-Type": "application/json" }) },
    );
    return response.data;
  },

  // EFS file manager
  async listEfsDir(root, path = "", { search = "", limit = 200, offset = 0 } = {}) {
    const response = await axios.get(`${API_BASE}/admin/efs/${root}/list/`, {
      params: { path, search, limit, offset },
      headers: await authHeaders(),
    });
    return response.data;
  },
  async downloadEfsFile(root, path) {
    const response = await axios.get(
      `${API_BASE}/admin/efs/${root}/download/`,
      {
        params: { path },
        responseType: "blob",
        headers: await authHeaders(),
      },
    );
    return response.data;
  },
  // Zipping a large folder runs as a background job (see job_manager on the
  // backend) instead of a single blocking request — startEfsDirZip returns
  // a job_id immediately, progress streams over the shared
  // ws/job_progress/{job_id}/ channel (via useJobProgress), and
  // downloadZipResult fetches the finished archive once the job completes.
  async startEfsDirZip(root, path) {
    const response = await axios.post(
      `${API_BASE}/admin/efs/${root}/zip/`,
      null,
      { params: { path }, headers: await authHeaders() },
    );
    return response.data;
  },
  async downloadZipResult(root, jobId) {
    const response = await axios.get(
      `${API_BASE}/admin/efs/${root}/zip/${jobId}/download/`,
      {
        responseType: "blob",
        headers: await authHeaders(),
        timeout: 120000,
      },
    );
    return response.data;
  },
  async uploadEfsFiles(root, path, fileList) {
    const formData = new FormData();
    Array.from(fileList).forEach((file) => formData.append("files", file));
    const response = await axios.post(
      `${API_BASE}/admin/efs/${root}/upload/`,
      formData,
      {
        params: { path },
        headers: await authHeaders({ "Content-Type": "multipart/form-data" }),
        timeout: 120000,
      },
    );
    return response.data;
  },
  // Upload-side counterpart to startEfsDirZip/downloadZipResult — for a
  // whole folder (with subfolders) instead of a handful of loose files.
  // Zip it locally, upload the single archive, the backend extracts it
  // preserving structure as a background job with the same job_id/progress
  // pattern as the folder-zip download.
  async startEfsZipUpload(root, path, zipFile) {
    const formData = new FormData();
    formData.append("zip_file", zipFile);
    const response = await axios.post(
      `${API_BASE}/admin/efs/${root}/upload-zip/`,
      formData,
      {
        params: { path },
        headers: await authHeaders({ "Content-Type": "multipart/form-data" }),
        timeout: 120000,
      },
    );
    return response.data;
  },
  async deleteEfsPath(root, path, recursive = false) {
    const response = await axios.delete(`${API_BASE}/admin/efs/${root}/delete/`, {
      data: { path, recursive },
      headers: await authHeaders({ "Content-Type": "application/json" }),
    });
    return response.data;
  },
};
